import type { ComponentType } from 'react'
import { useRef } from 'react'
import * as Yup from 'yup'
import { Box, Grid, Typography } from '@mui/material'
import { ErrorMessage, useField } from 'formik'
import { TFunction } from 'i18next'

import { cast } from '@dhl/utils'
import { useFocusOnFirstError } from '@dhl/use-focus-on-first-error'

import { OptionCard } from './OptionCard'
import { OptionValue } from './model'

type Props<A> = {
  name: string
  options: OptionValue<A>[]
  // eslint-disable-next-line react/no-unused-prop-types
  carrier?: string
  multiple?: boolean
  dataTestPrefix?: string
  icon: ComponentType<{ optionKey: string; carrier: string }>
  title: ComponentType<{ option: A }>
  description: ComponentType<{ option: A }>
}

export type OptionInputProps<TOption> = {
  option: TOption
}

export function OptionInput<A>({
  dataTestPrefix = '',
  name,
  options,
  multiple = false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  icon: Icon,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  description: Description,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  title: Title,
}: Props<A>) {
  const [field, , helpers] = useField<string | string[]>(name)
  const divRef = useRef<HTMLDivElement>(null)

  useFocusOnFirstError(name, divRef)

  const onChange = (key: string) => {
    const value = cast<string[]>(field.value)

    if (multiple) {
      const updatedValue = value.includes(key) ? value.filter((k) => k !== key) : [...value, key]

      const filteredByImplies = options
        .filter((o) => o.implies)
        .flatMap((o) => (o.implies || []).map((implies) => ({ key: o.key, implies })))
        .reduce((acc, option) => {
          if (option.implies === key && value.includes(option.key)) {
            return acc.filter((v) => v !== option.key)
          }

          if (option.key === key && value.includes(option.implies)) {
            return acc.filter((v) => v !== option.implies)
          }

          return acc
        }, updatedValue)

      helpers.setValue(filteredByImplies)
    } else {
      helpers.setValue(key)
    }
  }

  return (
    <div ref={divRef}>
      <Grid container justifyContent="space-between" direction="row" alignItems="stretch" spacing={1}>
        {options.map((option, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Grid size={{ lg: 6, xs: 12 }} key={`${index}-${option.key}`}>
            <OptionCard
              dataTestPrefix={dataTestPrefix}
              option={option}
              isSelected={multiple ? field.value.includes(option.key) : field.value === option.key}
              onClick={() => onChange(option.key)}
              description={Description}
              title={Title}
              icon={Icon}
            />
          </Grid>
        ))}
      </Grid>
      <ErrorMessage name={name}>
        {(errorMessage: string) => (
          <Box mt={2}>
            <Typography color="error">{errorMessage}</Typography>
          </Box>
        )}
      </ErrorMessage>
    </div>
  )
}

export const createValidationSchema = (t: TFunction) =>
  Yup.string().nullable().required(t('errorKeys.error_field_required'))
